import Vue from 'vue';
import Moment from 'moment';
import EdInputModel from '@/models/ed-input.model';
import EdFormModel from '@/models/ed-form.model';
import KEYS from '@/values/keys';
import { Component, Emit, Prop, Watch } from 'vue-property-decorator';
import { required, maxLength, numeric } from 'vuelidate/lib/validators';
import { CourseService } from '@/courses/services/courses.service';
import EdImageCreated from '@/content/pages/create-lesson/modals/ed-lesson-create-image/components/ed-image-created/ed-image-created.vue';
import { ALL_CONTENT_ROUTES } from '@/app/private/my-content/views/all/all.router';
import { COURSE_TEMPLATE_EVENTS, PRODUCT_TYPES, AUTOMATIC_FOLDER_BUILDER } from '@/course-templates/values/course-templates.values';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { IParamsCreateComponent } from '@/courses/interfaces/params-create-component.interface';
import { cloneDeep, isEqual } from 'lodash';
import { BUILDER_SCHOOL_DAY_TYPES, BUILDER_STATUS } from '@/courses/values/courses.values';

@Component({
  name: 'ed-create-product',
  components: {
    EdImageCreated
  }
})
export default class EdCreateProductComponent extends Vue {
  @Prop() readonly initFormData: any;
  @Prop() readonly coverImage: any;
  @Prop() readonly elementId: any;
  @Prop() readonly saving: boolean;
  @Prop() readonly title: string;
  @Prop({ default: false }) readonly isQuickSubject: boolean;
  @Prop() readonly typeSchoolDay: string;
  @Prop({ default: PRODUCT_TYPES.SUBJECT }) readonly productType: PRODUCT_TYPES;

  public form: any = null;
  public loading: boolean = false;
  public formData: any = {};
  public courseForm: any = {};
  public dropzoneMode: string = KEYS.DROPZONE_CONFIG.SINGLE;
  public uploadedImage: any = null;
  public subjects: Array<any> = [];
  public subjectSelected: string = '';
  public switchToggle = false;
  public processImage: boolean = false;
  public removeImage: boolean = false;
  public productTypes: any = PRODUCT_TYPES;
  public schoolCalendarList: any = [];
  public selectedSchoolCalendar: any = null;
  public initSchoolCalendar: any = null;
  public automaticFolder: string = AUTOMATIC_FOLDER_BUILDER.NO_AUTOMATIC;
  public automaticFolderTypes = AUTOMATIC_FOLDER_BUILDER;
  public components: IParamsCreateComponent[] = [];
  public componentsSelected: IParamsCreateComponent[] = [];
  public componentsSelectedOriginal: IParamsCreateComponent[] = [];
  public componentsChanged: boolean = false;
  public componentsChangedWarning: boolean = false;
  public automaticFolderChanged: boolean = false;
  public lessonVariance: number = 0;
  public lessonVarianceChanged: boolean = false;
  public calendarChangedWarning: boolean = false;
  public fitFirstAvailable = false;
  public fitFirstAvailableChanged: boolean = false;
  public componentsSelectedIds: number[] = [];

  /* Computed */
  get dropOptions(): any {
    return {
      url: 'http://eduac.com',
      autoProcessQueue: false,
      acceptedFiles: 'image/png,.jpg,.jpeg',
      uploadMultiple: false,
      addRemoveLinks: true,
      parallelUploads: 1,
      maxFiles: 1,
      createImageThumbnails: false,
      timeout: 600000
    }
  }

  get organization(): any {
    return this.$store.state.ToolsStore.institutionInfo;
  }

  get switchToggleTitle() {
    return this.switchToggle ? `${this.$i18n.t('courses.createCourse.onToggleGrading')}` : `${this.$i18n.t('courses.createCourse.offToggleGrading')}`;
  }

  /* Images Computed */
  get imagePreview(): any {
    if(this.uploadedImage) {
      return {
        hash: this.uploadedImage.hash || 0,
        thumbnailMediumUrl: URL.createObjectURL(this.uploadedImage.file),
        file: this.uploadedImage.file
      }
    } else return null
  }

  get gradeLevels() {
    return this.$store.state.CourseTemplateStore.templatesGradeLevelsOptions
      .filter(el => el.value);
  }

  get formDisabled() {
    let disabled = !this.form?.valid || this.saving || !this.subjectSelected;

    disabled = disabled || !this.selectedSchoolCalendar;
    
    return disabled;
  }

  get activeSchoolCalendar () {
    return this.$store.state.SchoolCalendarStore.schooldaysList.schoolDayId;
  }

  get disableSchoolCalendar () {
    return this.isQuickSubject || this.initFormData.status === BUILDER_STATUS.DONE;
  }

  get showFitFirstAvailable(){
    return this.isQuickSubject;
  }

  get showVariance () {
    return this.isQuickSubject && !this.fitFirstAvailable;
  }

  /* Methods */
  init(name: string = '', 
      description: string = '', 
      defaultDueDate:string = '3', 
      defaultLastDate: string = '0',
      actualGradeId = '',
      lessonVariance = 0
   ) {
    this.form = EdFormModel([
      EdInputModel('name', name, { required, maxLength: maxLength(200)}, true),
      EdInputModel('description', description),
      EdInputModel('defaultDueDate', defaultDueDate, { numeric }, true),
      EdInputModel('defaultLastDate', defaultLastDate, { numeric }, true),
      EdInputModel('actualGradeId', actualGradeId),
      EdInputModel('lessonVariance', lessonVariance),
    ]);
  }

  /* Image Methods */
  public async onImageUploadedSuccess({file, res}) {
    this.initFormData.id = res.id;
  }

  public onFileAdd(file: File) {
    let data = {
      file: file,
      hash: null
    }
    this.processImage = true;
    this.removeImage = false;
    this.uploadedImage = data;
  }

  public onError() {
    this.uploadedImage = null;
  }

  public removeImg(data: any) {
    this.uploadedImage && (this.$refs.dropzoneRef as any).removeFile(data.file);
    this.uploadedImage = null;
    this.removeImage = true;
  }

  public showErroModal() {
    this.$EdModal.createMessageModal({
      title: this.title,
      body: this.$i18n.t('courses.errors.errorSavingCover'),
      type: KEYS.EVENT_TYPES.DANGER
    });
  }
  /* End image Methods */

  goBack() {
    if(this.productType == this.productTypes.SUBJECT) 
      this.$router.push({ name: ALL_CONTENT_ROUTES.SUBJECTS.NAME });
    else 
      this.$router.push({ name: ALL_CONTENT_ROUTES.TEMPLATES.NAME });
  }

  async save(syncForm: boolean = false) {
    this.formData = {
      'categoryId': this.subjectSelected,
      'contentSummary': this.form.description.value,
      'contentTitle': this.form.name.value,
      'defaultDueDateForTasks': this.form.defaultDueDate.value,
      'defaultLastDateForTasks': this.form.defaultLastDate.value,
      'organizationId': this.organization.length > 0 ? this.organization[0].organizationId : '',
      'hasGrading': this.switchToggle,
      'deleteCover': this.removeImage,
    }
    
    this.formData.schoolDay = this.selectedSchoolCalendar;
    if(!this.processImage && this.initFormData.imgHash) {
      this.formData.coverImg = this.initFormData.imgHash;
      this.formData.coverImageUrl = this.initFormData.img;
    }

    this.formData.componentsSelected = this.componentsSelected;
    
    if(this.componentsSelectedOriginal?.length <= 0) {
      this.componentsSelectedOriginal = cloneDeep(this.componentsSelected);
    } 
    
    this.componentsChanged = (!isEqual(this.componentsSelected, this.componentsSelectedOriginal));
    this.componentsSelectedOriginal = cloneDeep(this.componentsSelected);
    this.componentsChangedWarning = false;

    if(this.initFormData.automaticFolderType !== this.automaticFolder) {
      this.automaticFolderChanged = true;
    } else {
      this.automaticFolderChanged = false;
    }
    this.formData.lessonVariance = this.form.lessonVariance.value;
    if(Number(this.initFormData.lessonVariance) !== Number(this.form.lessonVariance.value)) {
      this.lessonVarianceChanged = true;
    } else {
      this.lessonVarianceChanged = false;
    }
    
    this.formData.fitFirstAvailable = this.fitFirstAvailable;
    if(this.initFormData.fitFirstAvailable !== this.fitFirstAvailable) {
      this.fitFirstAvailableChanged = true;
    } else {
      this.fitFirstAvailableChanged = false;
    }

    this.formData.automaticFolderType = this.automaticFolder;
    this.formData.componentsChanged = this.componentsChanged;
    this.formData.existChanges = (this.automaticFolderChanged || this.fitFirstAvailableChanged || this.lessonVarianceChanged  || this.componentsChanged || this.initSchoolCalendar !== this.selectedSchoolCalendar);
    if(this.formData.existChanges) {
      this.formData.automaticFolderChanged = this.automaticFolderChanged;
      this.formData.fitFirstAvailableChanged = this.fitFirstAvailableChanged;
      this.formData.lessonVarianceChanged = this.lessonVarianceChanged;
      this.formData.componentsChanged = this.componentsChanged;
      this.formData.schoolCalendarChanged = (this.initSchoolCalendar !== this.selectedSchoolCalendar);
    }

    if(this.removeImage) {
      this.uploadedImage = null;
      this.formData.coverImg = null;
      this.formData.coverImageUrl = null;
    }

    let uploadedImage = this.uploadedImage;
    if(!this.processImage) uploadedImage = null;
    
    if(syncForm) {
      this.$edEventBus.$emit(COURSE_TEMPLATE_EVENTS.SAVE_SYNC_BUILDER_FORM, {formData: this.formData, uploadedImage});
    } else {
      this.$edEventBus.$emit(COURSE_TEMPLATE_EVENTS.SAVE_COURSE_FORM, {formData: this.formData, uploadedImage});
    }

    this.processImage = false;
    this.removeImage = false;
    this.initSchoolCalendar = this.selectedSchoolCalendar;
  }

  changeComponent() {
    this.componentsSelected = this.components.filter(c => this.componentsSelectedIds.includes(c.id));
    this.componentsChangedWarning = this.componentsSelectedOriginal.length > 0
      && !isEqual(this.componentsSelected, this.componentsSelectedOriginal);
  }

  @Watch('componentsSelectedIds')
  onComponentIdsChange() {
    this.changeComponent();
  }

  async setDataEdition(data) {
    this.init(
      data.title,
      data.contentSummary,
      data.defaultDueDateForTasks.toString(),
      data.defaultLastDateForTasks.toString(),
      data.actualGrade?.id || data.actualGradeId
    );
    
    this.switchToggle = data.hasGrading;
    this.selectedSchoolCalendar = this.initSchoolCalendar = (isNaN(data.schoolDay)) ? data.schoolDay.schoolDayId : data.schoolDay;
    this.automaticFolder = data.automaticFolderType ? data.automaticFolderType : this.automaticFolder;
    this.fitFirstAvailable = data.fitFirstAvailable ?? this.fitFirstAvailable;
    this.form.lessonVariance.value = data.lessonVariance ?? this.form.lessonVariance.value;


    if (data.img && data.imgHash) {
      this.setImageOnEdition(data.img, data.imgHash.substring(56))
    } else if(this.coverImage) {
      this.setImageOnEditionFromFile(this.coverImage);
    } else {
      this.loading = false;
    }
    
    this.components = data.components;

    if(data.componentsSelected && data.componentsSelected.length > 0) {
      this.componentsSelected = [...data.componentsSelected];
    } else {  
      let componentMandatory = this.components.find(c => c.mandatory);
      if (componentMandatory)
        this.componentsSelected = [componentMandatory]
    }

    this.componentsSelectedIds = this.componentsSelected.map(c => c.id);
    this.componentsSelectedOriginal = cloneDeep(this.componentsSelected);
    
    if(this.isQuickSubject && this.initFormData.schoolDay || this.initFormData.status === BUILDER_STATUS.DONE) {
      this.selectedSchoolCalendar = data.schoolDayComplate?.schoolDayId ?? data.schoolDay.schoolDayId;
    } else if (!this.selectedSchoolCalendar) {
      this.selectedSchoolCalendar = this.activeSchoolCalendar;
    }

    if(this.selectedSchoolCalendar)
      this.subjectDaysChanged();

    if(this.subjects.find(s => s.value === this.initFormData.categoryId)) {
      this.subjectSelected = data.categoryId;
    }
  }

  async setImageOnEditionFromFile(file) {
    this.uploadedImage = {file : file}
    this.loading = false;
    this.processImage = true;
  }

  async setImageOnEdition(url, imgHash){
    let request = await fetch(url);
    let blob: Blob = await request.blob();    
    let imageName = url.split('.').slice(0, -1).join('.');
    let image: File = new File([blob], imageName);
    this.uploadedImage = {file : image , hash: imgHash }
    this.loading = false;
  }

  async getSchoolCalendarList() {
    if(this.schoolCalendarList.length <= 0) {
      let orgCalendars = await this.$Builder.Services.updateSchoolCalendarListFromServer(this.organization[0].organizationId);
      let format = 'MMMM, YYYY';
      this.schoolCalendarList = orgCalendars.map(completeCalendar => {
        const calendar = completeCalendar.schoolDay;
        return { 
          text: Moment(calendar.startDate).format(format) + ' - ' +  Moment(calendar.endDate).format(format), 
          value: calendar.schoolDayId,
          duration: calendar.duration,
          startDate: calendar.startDate
        } 
      }); 

    }
  }

  subjectCalendarChanged() {
    this.calendarChangedWarning = (this.initSchoolCalendar !== this.selectedSchoolCalendar); 
    this.subjectDaysChanged();
  }

  created() {
    if(this.elementId) 
      this.loading = true;
    else 
      this.init();
    this.$edEventBus.$on(COURSES_EVENTS.SUBJECT_DESCRIPTION_BUILDER, this.save);
  }

  beforeDestroy() {
    this.$edEventBus.$off(COURSES_EVENTS.SUBJECT_DESCRIPTION_BUILDER, this.save);
  }

  @Emit('subjectDaysChanged')
  public subjectDaysChanged(){
    return this.selectedSchoolCalendar;
  }

  /* Watch */
  @Watch('organization', {immediate: true})
  async getData() {
    if(this.organization.length > 0) {
      this.organization && CourseService.getCourseCategories(this.organization[0].organizationId)
        .then((res) => {
          this.subjects = res.map(el => {
            return {
              text: el.name,
              value: el.id
            }
          })
        });
      if(this.typeSchoolDay === 'ORG') { 
        await this.getSchoolCalendarList();
      }

      if (!this.elementId) {
        const componentMandatory = this.components.find(c => c.mandatory);
        if (componentMandatory) {
          this.componentsSelected = [componentMandatory];
          this.componentsSelectedOriginal = [cloneDeep(componentMandatory)];
        }
      }
    }
  }

  @Watch('typeSchoolDay', {immediate: true})// This is to control while schoolday is loaded on data
  async getTypeSchoolDay() {
    if(this.typeSchoolDay === BUILDER_SCHOOL_DAY_TYPES.ORG) {
      this.getData();
    } else if(this.typeSchoolDay ===  BUILDER_SCHOOL_DAY_TYPES.CLASS || this.typeSchoolDay ===  BUILDER_SCHOOL_DAY_TYPES.SUBJECT) {
      this.setCalendarListForClass();
    }
  }

  @Watch('initFormData', {immediate: true, deep: true})
  setCalendarListForClass() {
    if(this.typeSchoolDay ===  BUILDER_SCHOOL_DAY_TYPES.CLASS || this.typeSchoolDay ===  BUILDER_SCHOOL_DAY_TYPES.SUBJECT) {
      if (this.isQuickSubject && this.initFormData.schoolDay || this.initFormData.status === BUILDER_STATUS.DONE) {
        let format = 'MMMM, YYYY';
        const schoolDayComplate = this.initFormData.schoolDayComplate ?? this.initFormData.schoolDay;
  
        this.schoolCalendarList = [{ 
          text: Moment(schoolDayComplate.startDate).format(format) + ' - ' +  Moment(schoolDayComplate.endDate).format(format), 
          value: schoolDayComplate.schoolDayId,
          duration: schoolDayComplate.duration,
          startDate: schoolDayComplate.startDate
        }];
  
        if(!this.selectedSchoolCalendar){
          this.selectedSchoolCalendar = schoolDayComplate.schoolDayId;
          this.subjectDaysChanged();
        }   
        
        if(this.initFormData.status === BUILDER_STATUS.DONE) {
          this.initSchoolCalendar = (isNaN(schoolDayComplate)) ? schoolDayComplate.schoolDayId : schoolDayComplate;
        }     
      } 
    }
    
    if(this.initFormData?.title) {
      this.setDataEdition(this.initFormData);
    }
  }

  @Watch('coverImage', {immediate: true, deep: true})
  setCoverImage() {
    if(this.coverImage) {
      this.setImageOnEditionFromFile(this.coverImage);
    }
  }

  @Watch('formDisabled', {immediate: true}) 
  emitDisabedFormEvent() {
    this.$emit('disableSubectForm', this.formDisabled);
  }

}

